
import {
    Multiselect,
    MultiselectProps,
    Select,
    SelectProps
} from '@cloudscape-design/components'; // Import both components
import React, { useEffect, useState } from 'react';
import { useGlobalState } from '../../pages/context/GlobalStateContext';
type ItemAccountPlan = {
    idAccountPlan: number;
    accountPlan: string;
    value: string;
    label: string;
};

interface SelectAccountPlanSingleProps {
    visible?: boolean;
    readonly?: boolean;
    value?: number | number[]; // Support both types
    width?: number;
    nullOption?: boolean;
    single?: boolean;
    tokenLimit?: number;
    hideTokens?: boolean;
    onSelect?: (selected: number | null) => void;
    filtering?: SelectProps.FilteringType; // Add filtering prop
}

interface SelectAccountPlanMultiProps {
    visible?: boolean;
    readonly?: boolean;
    value?: number | number[]; // Support both types
    width?: number;
    nullOption?: boolean;
    single?: boolean;
    tokenLimit?: number;
    hideTokens?: boolean;
    onSelect?: (selected: number[]) => void;
    filtering?: SelectProps.FilteringType; // Add filtering prop
}

export type SelectAccountPlanProps =
    | SelectAccountPlanSingleProps
    | SelectAccountPlanMultiProps;

const SelectAccountPlan: React.FC<SelectAccountPlanProps> = ({
    value,
    nullOption,
    readonly = false,
    single = true,
    tokenLimit = 3,
    hideTokens = false,
    filtering = "none",
    onSelect
}) => {
    const { data } = useGlobalState();
    if (!data || !data.accountPlans) return <></>;

    const mappedAccountPlans: ItemAccountPlan[] = [
        ...(nullOption && single
            ? [{ idAccountPlan: 0, accountPlan: "No seleccionado", value: "0", label: "No seleccionado" }]
            : []),
        ...data.accountPlans.map((plan: ItemAccountPlan) => ({
            idAccountPlan: plan.idAccountPlan,
            accountPlan: plan.accountPlan,
            value: plan.idAccountPlan.toString(),
            label: plan.accountPlan,
        })),
    ];

    // State for single or multi
    const [selectedSingle, setSelectedSingle] = useState<ItemAccountPlan | null>(null);
    //const [selectedMulti, setSelectedMulti] = useState<ItemAccountPlan[]>([]);
    const [selectedMulti, setSelectedMulti] = useState<ItemAccountPlan[]>([]);



    // --- Single Select Handler ---
    const handleSingleChange: SelectProps['onChange'] = ({ detail }) => {
        const selectedOption = detail.selectedOption as ItemAccountPlan | null;

        setSelectedSingle(selectedOption);

        if (onSelect && single) {
            const selectedId = selectedOption?.idAccountPlan ?? null; // Extract ID
            // Assert the function signature for single mode
            (onSelect as (selected: number | null) => void)(selectedId);
        }
    };

    // --- Multi Select Handler ---
    const handleMultiChange: MultiselectProps['onChange'] = ({ detail }) => {
        const list = detail.selectedOptions as ItemAccountPlan[];

        // Use spread to ensure setting a new array reference
        setSelectedMulti([...list]);

        if (onSelect && !single) {
            const selectedIds = list.map(item => item.idAccountPlan); // Extract array of IDs
            // Assert the function signature for multi mode
            (onSelect as (selected: number[]) => void)(selectedIds);
        }
    };
    useEffect(() => {
        if (single && typeof value === 'number') {
            setSelectedSingle(mappedAccountPlans.find(p => p.idAccountPlan === value) || null);
        } else if (!single && Array.isArray(value)) {
            setSelectedMulti(mappedAccountPlans.filter(p => value.includes(p.idAccountPlan)));
        }
    }, [value, single]);


    const placeholder = single ? "Seleccione un plan de cuenta" : selectedMulti && selectedMulti.length > 0 ? `${selectedMulti.length} seleccionado${selectedMulti.length > 1 ? 's' : ''}` : "Seleccionar planes de cuenta";
    return (
        <div style={{ width: '200px' }}>
            {single ? (
                // SINGLE SELECTION (Cloudscape Select)
                <Select
                    filteringType={filtering}
                    readOnly={readonly}
                    placeholder={placeholder}
                    selectedOption={selectedSingle}
                    options={mappedAccountPlans}
                    onChange={handleSingleChange}
                />
            ) : (
                // MULTI SELECTION (Cloudscape Multiselect)
                <Multiselect
                    filteringType={filtering}
                    tokenLimit={tokenLimit}
                    hideTokens={hideTokens}
                    readOnly={readonly}
                    placeholder={placeholder}
                    selectedOptions={selectedMulti}
                    options={mappedAccountPlans}
                    onChange={handleMultiChange}
                />
            )}
        </div>
    );
};

export default SelectAccountPlan;